/**
 * Buy Now - direct-to-checkout CTA for the Anime Finds product form.
 *
 * Submits the real product form so app-injected fields and line item properties are
 * preserved, then sends the customer straight to checkout. It deliberately bypasses
 * the theme's own submit handler, so the cart drawer never opens.
 *
 * Delegated from the document so it keeps working if a section is re-rendered.
 */

if (!window.__afBuyNowBound) {
  window.__afBuyNowBound = true;

  var ERROR_DISPLAY_MS = 8000;

  // The theme's Add to cart button for this form - the source of truth for availability.
  var submitButtonFor = function (button) {
    var selector = button.dataset.submitSelector;
    var fromSelector = selector ? document.querySelector(selector) : null;
    if (fromSelector) return fromSelector;
    var form = button.closest('form');
    return form ? form.querySelector('[name="add"]') : null;
  };

  var errorElement = function (button) {
    var id = button.getAttribute('aria-describedby');
    return id ? document.getElementById(id) : null;
  };

  var showError = function (button, message) {
    var element = errorElement(button);
    if (!element) return;
    element.textContent = message;
    element.hidden = false;
    clearTimeout(Number(button.dataset.errorTimeout));
    button.dataset.errorTimeout = String(
      setTimeout(function () {
        element.hidden = true;
        element.textContent = '';
      }, ERROR_DISPLAY_MS)
    );
  };

  var clearError = function (button) {
    var element = errorElement(button);
    if (!element) return;
    clearTimeout(Number(button.dataset.errorTimeout));
    element.hidden = true;
    element.textContent = '';
  };

  var setLoading = function (button, loading) {
    button.classList.toggle('af-buy-now--loading', loading);
    if (loading) {
      button.dataset.busy = 'true';
      button.setAttribute('aria-busy', 'true');
    } else {
      delete button.dataset.busy;
      button.removeAttribute('aria-busy');
    }
  };

  /*
   * Dawn does not re-render the buy buttons when the variant changes - it only toggles
   * the Add to cart button's disabled state. Mirror that onto Buy Now so sold-out
   * variants disable it without a page reload.
   */
  var syncAvailability = function (button) {
    var submit = submitButtonFor(button);
    if (!submit) return;
    var unavailable = submit.hasAttribute('disabled');
    button.disabled = unavailable;
    if (unavailable) {
      button.setAttribute('aria-disabled', 'true');
    } else {
      button.removeAttribute('aria-disabled');
    }
  };

  var observeAvailability = function () {
    document.querySelectorAll('[data-af-buy-now]').forEach(function (button) {
      if (button.dataset.afObserved === 'true') return;
      var submit = submitButtonFor(button);
      if (!submit) return;
      button.dataset.afObserved = 'true';
      syncAvailability(button);
      new MutationObserver(function () {
        syncAvailability(button);
      }).observe(submit, { attributes: true, attributeFilter: ['disabled'] });
    });
  };

  var buyNow = function (button) {
    if (button.dataset.busy === 'true') return;
    if (button.disabled || button.getAttribute('aria-disabled') === 'true') return;

    var form = button.closest('form[data-type="add-to-cart-form"]') || button.closest('form');
    if (!form) {
      showError(button, button.dataset.errorGeneric);
      return;
    }

    var body = new FormData(form);

    // Dawn renders the variant input as `disabled`, so FormData skips it.
    // Read it directly and set it explicitly.
    if (!body.get('id')) {
      var variantInput =
        form.querySelector('.product-variant-id') || form.querySelector('[name="id"]');
      if (!variantInput || !variantInput.value) {
        showError(button, button.dataset.errorGeneric);
        return;
      }
      body.set('id', variantInput.value);
    }

    if (!body.get('quantity')) body.set('quantity', '1');

    clearError(button);
    setLoading(button, true);

    fetch(button.dataset.cartAddUrl || '/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: body,
    })
      .then(function (response) {
        return response
          .json()
          .catch(function () {
            return null;
          })
          .then(function (data) {
            if (!response.ok) {
              throw new Error(
                (data && (data.description || data.message)) || button.dataset.errorGeneric
              );
            }
            // Full navigation keeps Shopify Markets / locale routing intact.
            window.location.assign(button.dataset.checkoutUrl || '/checkout');
          });
      })
      .catch(function (error) {
        setLoading(button, false);
        showError(button, (error && error.message) || button.dataset.errorGeneric);
      });
  };

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;
    var button = target.closest('[data-af-buy-now]');
    if (!button) return;
    if (window.Shopify && window.Shopify.designMode) return;
    event.preventDefault();
    buyNow(button);
  });

  // A customer who reaches checkout and presses Back can get a bfcache page with the
  // button frozen mid-request.
  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;
    document.querySelectorAll('[data-af-buy-now]').forEach(function (button) {
      setLoading(button, false);
      clearError(button);
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeAvailability);
  } else {
    observeAvailability();
  }
  document.addEventListener('shopify:section:load', observeAvailability);
}
